import { EmptyState, FormCard } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Divider, Tag } from 'antd'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { getNominative } from 'common/utils'
import Description from '@lmc/ui-kit/src/dataDisplay/Descriptions/Description'
import { useSelector } from 'react-redux'
import {
  Home,
  IncidentReport,
  IncidentSeverityValues,
  FollowUpActionStatus,
  Carer
} from '@lmc/ts-common'
import {
  getAdditionalItems,
  getDamagesItems,
  getDebriefingItems,
  getIncidentDetailsItems,
  getInjuriesItems,
  getLearningAndOutcomesItems,
  getMedicationsItems,
  getOpinionItems,
  getRestrictivePracticesItems
} from '../../utils/viewCaseItems'
import { ListItem, NoDataIcon } from '@lmc/ui-kit/src/dataDisplay'
import useBodyMaps from 'hooks/useBodyMaps'
import useFeatureFlags from 'hooks/useFeatureFlags'

type IncidentCaseViewProps = {
  incidentReport: IncidentReport
  severities: IncidentSeverityValues | undefined
  testId?: string
}

const labelStyle = { width: 270 }

export const IncidentCaseView = ({
  incidentReport,
  severities,
  testId = 'incident-case-view'
}: IncidentCaseViewProps) => {
  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )
  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { bodyMaps } = useBodyMaps({
    residentId: incidentReport.serviceUser.id
  })

  const flags = useFeatureFlags({
    flagNames: ['incident_case_action_assignment']
  })?.data
  const flagIncidentCaseActionAssignment =
    flags?.incident_case_action_assignment?.enabled

  const incidentDetailsItems = getIncidentDetailsItems(
    incidentReport,
    severities,
    getNominative(preferredPeopleNominative, 'Residents'),
    t
  )

  const restrictivePracticesItems = getRestrictivePracticesItems(
    incidentReport,
    t
  )

  const injuriesItems = getInjuriesItems(
    incidentReport,
    getNominative(preferredPeopleNominative),
    bodyMaps,
    t
  )

  const damagesItems = getDamagesItems(incidentReport, t)

  const medicationItems = getMedicationsItems(incidentReport, t)

  const opinionItems = getOpinionItems(incidentReport, t)

  const additionalItems = getAdditionalItems(incidentReport, t)

  const debriefingItems = getDebriefingItems(incidentReport, t)

  const learningAndOutcomesItems = getLearningAndOutcomesItems(
    incidentReport,
    t
  )

  const hasIncidentDetails =
    incidentDetailsItems.length > 0 ||
    restrictivePracticesItems.length > 0 ||
    injuriesItems.length > 0 ||
    damagesItems.length > 0 ||
    medicationItems.length > 0 ||
    additionalItems.length > 0 ||
    opinionItems.length > 0 ||
    learningAndOutcomesItems.length > 0

  const hasFollowUpActions =
    incidentReport?.case?.followUpActions?.actions &&
    incidentReport?.case?.followUpActions?.actions.length > 0

  const hasDebriefingItems = debriefingItems.length > 0

  const hasLearningAndOutcomes = learningAndOutcomesItems.length > 0

  const hasCaseInfo =
    hasIncidentDetails || hasFollowUpActions || hasDebriefingItems

  return (
    <Wrapper data-testid={testId}>
      <Title level={2} data-testid={`${testId}-title`}>
        {t(incidentReportsConstants.INCIDENT_CASE)}
      </Title>
      {hasIncidentDetails && (
        <FormCard
          testId={`${testId}-incident-details`}
          title={t(incidentReportsConstants.INCIDENT_DETAILS)}
        >
          {incidentDetailsItems.length > 0 && (
            <Description
              data-testid={`${testId}-incident-details-description`}
              labelStyle={labelStyle}
              items={incidentDetailsItems}
              bordered
              column={1}
            />
          )}
          {restrictivePracticesItems.length > 0 && (
            <SectionContainer data-testid={`${testId}-restrictive-practices`}>
              <FormCardHeading
                level={4}
                data-testid={`${testId}-restrictive-practices-heading`}
              >
                {t(incidentReportsConstants.RESTRICTIVE_PRACTICES)}
              </FormCardHeading>
              <StyledDivider />
              <Description
                data-testid={`${testId}-restrictive-practices-description`}
                labelStyle={labelStyle}
                items={restrictivePracticesItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}
          {injuriesItems.length > 0 && (
            <SectionContainer data-testid={`${testId}-injuries`}>
              <FormCardHeading
                level={4}
                data-testid={`${testId}-injuries-heading`}
              >
                {t(incidentReportsConstants.INJURIES)}
              </FormCardHeading>
              <StyledDivider />
              <Description
                data-testid={`${testId}-injuries-description`}
                labelStyle={labelStyle}
                items={injuriesItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}

          {damagesItems.length > 0 && (
            <SectionContainer data-testid={`${testId}-damages`}>
              <FormCardHeading
                level={4}
                data-testid={`${testId}-damages-heading`}
              >
                {t(incidentReportsConstants.DAMAGES)}
              </FormCardHeading>
              <StyledDivider />
              <Description
                data-testid={`${testId}-damages-description`}
                labelStyle={labelStyle}
                items={damagesItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}

          {medicationItems.length > 0 && (
            <SectionContainer data-testid={`${testId}-medications`}>
              <FormCardHeading
                level={4}
                data-testid={`${testId}-medications-heading`}
              >
                {t(incidentReportsConstants.MEDICATIONS)}
              </FormCardHeading>
              <StyledDivider />
              <Description
                data-testid={`${testId}-medications-description`}
                labelStyle={labelStyle}
                items={medicationItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}

          {additionalItems.length > 0 && (
            <SectionContainer data-testid={`${testId}-additional-comment`}>
              <FormCardHeading
                level={4}
                data-testid={`${testId}-additional-comment-heading`}
              >
                {t(incidentReportsConstants.ADDITIONAL_COMMENT)}
              </FormCardHeading>
              <StyledDivider />
              <Description
                data-testid={`${testId}-additional-comment-description`}
                labelStyle={labelStyle}
                items={additionalItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}

          {opinionItems.length > 0 && (
            <SectionContainer data-testid={`${testId}-opinion`}>
              <FormCardHeading
                level={4}
                data-testid={`${testId}-opinion-heading`}
              >
                {t(incidentReportsConstants.SU_OPINION, {
                  variable: getNominative(preferredPeopleNominative)
                })}
              </FormCardHeading>
              <StyledDivider />
              <Description
                data-testid={`${testId}-opinion-description`}
                labelStyle={labelStyle}
                items={opinionItems}
                bordered
                column={1}
              />
            </SectionContainer>
          )}
        </FormCard>
      )}
      {hasFollowUpActions && (
        <ActionsFormCard
          testId={`${testId}-follow-up-actions`}
          title={t(incidentReportsConstants.FOLLOW_UP_ACTIONS)}
        >
          <ActionsList data-testid={`${testId}-follow-up-actions-list`}>
            {incidentReport.case?.followUpActions.actions.map(
              (action, index) => {
                const assignedCarer = action.assignedUserId
                  ? carers?.find(
                      (carer) => carer.id.toString() === action.assignedUserId
                    )
                  : null
                const displayName =
                  assignedCarer?.name?.first && assignedCarer?.name?.last
                    ? `${assignedCarer.name.first} ${assignedCarer.name.last}`
                    : null

                return (
                  <ActionListItem
                    key={action.id || action.title}
                    data-testid={`${testId}-follow-up-actions-item-${index}`}
                    title={
                      <Text
                        data-testid={`${testId}-follow-up-actions-item-${index}-title`}
                      >
                        {action.title}
                      </Text>
                    }
                    rightChildren={
                      <RightContentWrapper>
                        {flagIncidentCaseActionAssignment ? (
                          <OwnershipWrapper
                            data-testid={`${testId}-follow-up-actions-item-${index}-owner`}
                          >
                            <Text level={1} weight="regular">
                              {displayName
                                ? `${t(incidentReportsConstants.OWNED_BY)}\u00A0`
                                : `${t(incidentReportsConstants.UNASSIGNED)}`}
                            </Text>
                            {displayName && (
                              <Text level={1} weight="bold">
                                {displayName}
                              </Text>
                            )}
                          </OwnershipWrapper>
                        ) : null}
                        <Tag
                          data-testid={`${testId}-follow-up-actions-item-${index}-status`}
                        >
                          <Text>
                            {t(
                              incidentReportsConstants[
                                FollowUpActionStatus[action.status]
                              ]
                            ) ||
                              t(
                                incidentReportsConstants[
                                  FollowUpActionStatus.notStarted
                                ]
                              )}
                          </Text>
                        </Tag>
                      </RightContentWrapper>
                    }
                  />
                )
              }
            )}
          </ActionsList>
        </ActionsFormCard>
      )}
      {hasDebriefingItems && (
        <FormCard
          testId={`${testId}-debriefing`}
          title={t(incidentReportsConstants.DEBRIEFING)}
        >
          <Description
            data-testid={`${testId}-debriefing-description`}
            labelStyle={labelStyle}
            items={debriefingItems}
            bordered
            column={1}
          />
        </FormCard>
      )}
      {hasLearningAndOutcomes && (
        <FormCard
          testId={`${testId}-learning-outcomes`}
          title={t(incidentReportsConstants.LEARNING_OUTCOMES)}
        >
          <Description
            data-testid={`${testId}-learning-outcomes-description`}
            labelStyle={labelStyle}
            items={learningAndOutcomesItems}
            bordered
            column={1}
          />
        </FormCard>
      )}
      {!hasCaseInfo && (
        <EmptyState
          data-testid={`${testId}-empty-state`}
          icon={<NoDataIcon />}
          title={t(incidentReportsConstants.EMPTY_CASE_TITLE)}
          description={t(incidentReportsConstants.EMPTY_CASE_DESCRIPTION)}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding-bottom: 64px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormCardHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const ActionsFormCard = styled(FormCard)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ActionListItem = styled(ListItem)`
  background-color: ${({ theme }) => theme.color.neutral.color10};
  padding: 24px 16px;
`

const ActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledDivider = styled(Divider)`
  color: ${({ theme }) => theme.color.neutral.color8};
  margin: 0px;
`

const Title = styled(Heading)``

const RightContentWrapper = styled.div`
  display: flex;
  gap: 18px;
  justify-content: end;
  align-items: center;
`

const OwnershipWrapper = styled.div`
  display: flex;
  gap: 2px;
  align-items: flex-start;

  > div {
    color: ${({ theme }) => theme.color.neutral.color3};
  }
`
